import type { BreadcrumbList, ListItem, Offer, Organization, Place, PostalAddress, WithContext } from 'schema-dts';
import type { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import {
    SCHEMA_CONTACT_FALLBACK,
    SCHEMA_OFFER_AVAILABILITY,
    SCHEMA_ORGANIZATION_NAME,
    SCHEMA_PLACE_NAME,
    getSchemaIds,
    getSchemaSiteUrl,
    schemaAbsoluteUrl,
} from '@/shared/lib/utils/schema-ids';

export type SchemaContacts = {
    phone?: string | null;
    email?: string | null;
    street?: string | null;
    city?: string | null;
    postalCode?: string | null;
};

export type SchemaOfferInput = {
    name?: string | null;
    price?: string | null;
    url?: string | null;
};

export function stripHtml(html: string): string {
    return html
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function oneLine(text: string): string {
    return text.replace(/\s+/g, ' ').trim();
}

export function containsExpoName(text: string): boolean {
    return /expo/i.test(text);
}

export function schemaTextWithoutExpo(text: string | null | undefined, fallback: string): string {
    const cleaned = oneLine(stripHtml(text || ''));
    if (!cleaned || containsExpoName(cleaned)) return fallback;
    return cleaned;
}

function pickContact(value: string | null | undefined, fallback: string): string {
    const trimmed = value?.trim();
    return trimmed ? trimmed : fallback;
}

export function parseOfferPrice(value?: string | null): string | null {
    if (!value) return null;
    const match = value.replace(/[\s\u00a0]/g, '').match(/\d+/);
    return match ? match[0] : null;
}

export function buildSchemaOffers(inputs: SchemaOfferInput[]): Offer[] {
    const offers: Offer[] = [];
    for (const input of inputs) {
        const price = parseOfferPrice(input.price);
        if (!price) continue;
        const name = input.name?.trim();
        if (!name) continue;
        offers.push({
            '@type': 'Offer',
            name,
            price,
            priceCurrency: 'RUB',
            availability: SCHEMA_OFFER_AVAILABILITY,
            url: schemaAbsoluteUrl(input.url?.trim() || '/registration/'),
        });
    }
    return offers;
}

export function buildPostalAddress(contacts?: SchemaContacts | null): PostalAddress {
    return {
        '@type': 'PostalAddress',
        streetAddress: pickContact(contacts?.street, SCHEMA_CONTACT_FALLBACK.streetAddress),
        addressLocality: pickContact(contacts?.city, SCHEMA_CONTACT_FALLBACK.addressLocality),
        postalCode: pickContact(contacts?.postalCode, SCHEMA_CONTACT_FALLBACK.postalCode),
        addressCountry: SCHEMA_CONTACT_FALLBACK.addressCountry,
    };
}

export function buildSchemaPlace(contacts?: SchemaContacts | null): Place {
    return {
        '@type': 'Place',
        name: SCHEMA_PLACE_NAME,
        address: buildPostalAddress(contacts),
    };
}

export function buildSchemaOrganization(contacts?: SchemaContacts | null): WithContext<Organization> {
    const ids = getSchemaIds();
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': ids.organization,
        name: SCHEMA_ORGANIZATION_NAME,
        url: getSchemaSiteUrl(),
        telephone: pickContact(contacts?.phone, SCHEMA_CONTACT_FALLBACK.telephone),
        email: pickContact(contacts?.email, SCHEMA_CONTACT_FALLBACK.email),
        address: buildPostalAddress(contacts),
    };
}

export function schemaIdRef(id: string): { '@id': string } {
    return { '@id': id };
}

export function schemaEventImage(src?: string | null): string | undefined {
    return schemaAbsoluteUrl(src || '/img/og-image.jpg') || undefined;
}

export function generateBreadcrumbListJsonLd(breadcrumbs: AppBreadcrumbs): WithContext<BreadcrumbList> {
    const itemListElement: ListItem[] = breadcrumbs.map((item, index) => {
        const href = item.href?.trim();
        const url = href ? schemaAbsoluteUrl(href) : undefined;
        const li: ListItem = {
            '@type': 'ListItem',
            position: index + 1,
            name: item.text,
            ...(url ? { item: url } : {}),
        };
        return li;
    });

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement,
    };
}
